import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Trash2, ArrowLeft } from "lucide-react";

const COLUMNS = [
  { key: "todo", label: "To Do", suit: "♠", color: "border-neutral-700" },
  { key: "in_progress", label: "In Play", suit: "♥", color: "border-amber-500/70" },
  { key: "done", label: "Closed", suit: "♦", color: "border-green-500/70" },
];

const PRIO_COLORS = {
  low: "text-neutral-400 border-neutral-700",
  medium: "text-amber-400 border-amber-600/40",
  high: "text-red-400 border-red-600/40",
  critical: "text-red-500 border-red-600",
};

export default function ProjectKanban() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [creating, setCreating] = useState(null);
  const [newTask, setNewTask] = useState({ title: "", priority: "medium" });
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const [p, t] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/tasks?project_id=${id}`),
    ]);
    setProject(p.data);
    setTasks(t.data);
  };

  useEffect(() => {
    load();
  }, [id]);

  const addTask = async (status) => {
    if (!newTask.title.trim()) return;
    await api.post("/tasks", { project_id: id, title: newTask.title, status, priority: newTask.priority });
    setNewTask({ title: "", priority: "medium" });
    setCreating(null);
    load();
  };

  const moveTo = async (taskId, status) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status } : t)));
    await api.put(`/tasks/${taskId}`, { status });
  };

  const removeTask = async (taskId) => {
    await api.delete(`/tasks/${taskId}`);
    load();
  };

  if (!project) return <div className="p-12 label-mono">Loading table…</div>;

  return (
    <div className="p-8 lg:p-12">
      <Link to="/app/projects" data-testid="kanban-back" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-amber-400">
        <ArrowLeft size={12} /> All Projects
      </Link>
      <header className="mb-10 flex items-end justify-between">
        <div>
          <div className="label-mono text-green-400 mb-2">// table.{project.name.toLowerCase().replace(/\s+/g, "_")}</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="kanban-title">{project.name}</h1>
          <p className="text-neutral-400 text-sm mt-2">{project.description || "No briefing."}</p>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <span className="label-mono">{tasks.length} hands</span>
          <span className="w-3 h-3" style={{ background: project.color }} />
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col.key);
          return (
            <div
              key={col.key}
              data-testid={`kanban-column-${col.key}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragId) {
                  moveTo(dragId, col.key);
                  setDragId(null);
                }
              }}
              className={`panel p-4 min-h-[60vh] border-t-2 ${col.color}`}
            >
              <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                  <span className="font-serif text-xl">{col.label}</span>
                  <span className="label-mono">{col.suit}</span>
                </div>
                <span className="label-mono">{colTasks.length}</span>
              </div>

              <div className="space-y-3">
                {colTasks.map((t, i) => (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    data-testid={`kanban-task-${t.id}`}
                    className={`panel p-3 card-suit cursor-grab active:cursor-grabbing deal ${dragId === t.id ? "dragging" : ""}`}
                    data-suit={col.suit}
                    style={{ animationDelay: `${i * 35}ms` }}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm mb-2">{t.title}</div>
                        <div className="flex items-center gap-2">
                          <span className={`label-mono px-2 py-[2px] border ${PRIO_COLORS[t.priority] || ""}`}>{t.priority}</span>
                        </div>
                      </div>
                      <button data-testid={`kanban-task-delete-${t.id}`} onClick={() => removeTask(t.id)} className="text-neutral-600 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}

                {creating === col.key ? (
                  <div className="panel p-3">
                    <input
                      autoFocus
                      data-testid={`kanban-new-input-${col.key}`}
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && addTask(col.key)}
                      placeholder="Task title…"
                      className="input-shell w-full px-2 py-2 text-sm font-jet mb-2"
                    />
                    <div className="flex items-center gap-2">
                      <select
                        data-testid={`kanban-new-priority-${col.key}`}
                        value={newTask.priority}
                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                        className="input-shell text-xs px-2 py-1 font-jet"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                      <button data-testid={`kanban-new-save-${col.key}`} onClick={() => addTask(col.key)} className="btn-primary text-[10px] px-3 py-1">Deal</button>
                      <button data-testid={`kanban-new-cancel-${col.key}`} onClick={() => setCreating(null)} className="btn-ghost text-[10px] px-3 py-1">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid={`kanban-add-${col.key}`}
                    onClick={() => setCreating(col.key)}
                    className="w-full border border-dashed border-neutral-800 hover:border-green-500/60 text-neutral-500 hover:text-green-400 transition py-3 text-xs font-jet uppercase tracking-[0.18em] flex items-center justify-center gap-2"
                  >
                    <Plus size={12} /> Add card
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
